import React from 'react';
import {View, Pressable} from 'react-native';
import moment from 'moment';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';

import Text from '../../components/elements/Text';
import Notes from '../../components/modules/Notes';

import {DLog} from '../../utils/HelperMethods';
import {LeftArrowIcon, RightArrowIcon} from '../../utils/SvgIcons';

//called when closing the notes calendar
export const onCalendarClose = (dates, range, setRange, setCalendarVisible) => {
  if (dates) {
    // DLog(
    //   'dates are',
    //   dates.toDate.substring(0, 4),
    //   dates.toDate.substring(5, 7) - 1,
    //   dates.toDate.substring(8, 10),
    // );
    let startDate = new Date(
      dates.toDate.substring(0, 4),
      dates.toDate.substring(5, 7) - 1,
      dates.toDate.substring(8, 10),
    );
    let endDate = new Date(
      dates.fromDate.substring(0, 4),
      dates.fromDate.substring(5, 7) - 1,
      dates.fromDate.substring(8, 10),
    );
    //difference between dates
    let diff = startDate.getTime() - endDate.getTime();
    let days = Math.floor(diff / (1000 * 60 * 60 * 24));
    // DLog(startDate, endDate, days);
    let tempRange = {...range};
    tempRange.startDate = startDate;
    tempRange.endDate = endDate;
    tempRange.numDays = days + 1;
    // DLog('temprange is',tempRange);
    setRange(tempRange);
  }

  setCalendarVisible(false);
};

//updates the filter date for my daily trends graph
export const updateFilterDate = (val, range, filterDate, setFilterDate) => {
  let tempFilterDate = new Date(filterDate);
  //update date by val
  tempFilterDate.setDate(tempFilterDate.getDate() + val);
  if (
    tempFilterDate.toISOString() >= range.endDate.toISOString() &&
    tempFilterDate.toISOString() <= range.startDate.toISOString()
  ) {
    setFilterDate(tempFilterDate.toISOString());
  }
  // DLog('filter date is', tempFilterDate);
};

//config for swipe actions in my daily trends graph
export const config = {
  velocityThreshold: 0.3,
  directionalOffsetThreshold: 80,
};

//get range to show in the mini calendar
//(plain helper, not a component — the caller passes its themed styles/colors)
const getRange = (range, styles, colors) => {
  // DLog('range', endDate.toDateString(), startDate.toDateString());
  if (range.endDate.getFullYear() == range.startDate.getFullYear()) {
    // DLog(endDate.toDateString().substring(4, 10));
    return (
      <View style={styles.midTabStyle}>
        <Text variation="Body1SemiBold" color={colors.textHeading}>
          {range.endDate.toDateString().substring(4, 10)}
          {' - '}
          {range.startDate.toDateString().substring(4, 10)}
        </Text>
        <Text variation="Body1Regular" color={colors.textHeading}>
          {' ' + range.startDate.getFullYear().toString()}
        </Text>
      </View>
    );
  } else {
    return (
      <View style={styles.midTabStyle}>
        <Text variation="Body1SemiBold" color={colors.textHeading}>
          {range.endDate.toDateString().substring(4, 15)}
          {' - '}
          {range.startDate.toDateString().substring(4, 15)}
        </Text>
      </View>
    );
  }
};

//update the range shown in the mini calendar
const updateRange = (val, range, setRange) => {
  //DLog('current range is', range);
  let tempRange = {...range};
  let currentStartDate = new Date(range.startDate);
  currentStartDate.setDate(currentStartDate.getDate() + val);
  tempRange.startDate = currentStartDate;
  let currentEndDate = new Date(range.endDate);
  currentEndDate.setDate(currentEndDate.getDate() + val);
  tempRange.endDate = currentEndDate;
  //DLog('updated range is', tempRange);
  if (tempRange.startDate.getTime() <= new Date().getTime()) {
    setRange(tempRange);
  }
};

//Mini calendar on top of UI
export const MiniCalendar = ({
  homeMode,
  range,
  setRange,
  setCalendarVisible,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View style={styles.miniCalendar}>
      <Pressable
        onPress={() => {
          updateRange(-range.numDays, range, setRange);
        }}
        style={styles.calendarMovePressable}>
        <LeftArrowIcon width={9} height={16} tintColor={colors.textDisabled} />
      </Pressable>
      {homeMode ? (
        <View style={styles.midTabStyle}>
          <Text variation="Body1SemiBold" color={colors.textHeading}>
            {range.startDate.toDateString().substring(0, 3)}
          </Text>
          <Text variation="Body1Regular" color={colors.textMuted}>
            {' ' +
              (range.startDate.getMonth() + 1) +
              '/' +
              range.startDate.getDate() +
              '/' +
              range.startDate.getFullYear().toString().substring(2, 4)}
          </Text>
        </View>
      ) : (
        <Pressable
          style={styles.fullFlex}
          onPress={() => setCalendarVisible(true)}>
          {getRange(range, styles, colors)}
        </Pressable>
      )}
      <Pressable
        onPress={() => {
          updateRange(range.numDays, range, setRange);
        }}
        style={styles.calendarMovePressable}>
        <RightArrowIcon width={9} height={16} tintColor={colors.textDisabled} />
      </Pressable>
    </View>
  );
};

export const WebArrows = ({onPressPrev, onPressNext}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View style={styles.webArrowStyle}>
      <Pressable onPress={onPressPrev} style={styles.calendarMovePressable}>
        <LeftArrowIcon width={9} height={16} tintColor={colors.textDisabled} />
      </Pressable>
      <Pressable onPress={onPressNext} style={styles.calendarMovePressable}>
        <RightArrowIcon width={9} height={16} tintColor={colors.textDisabled} />
      </Pressable>
    </View>
  );
};

//sets range to one of the preset 7,30 or 60 days
export const DaysSelector = ({range, setNumDays}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View style={styles.daysSelector}>
      <Pressable onPress={() => setNumDays(7)}>
        <View style={styles.dayStyle(range.numDays == 7)}>
          <Text
            variation={
              range.numDays == 7 ? 'Subtitle1Bold' : 'Subtitle1Regular'
            }
            color={range.numDays == 7 ? colors.accentStrong : colors.textMuted}>
            7 Days
          </Text>
        </View>
      </Pressable>
      <Pressable onPress={() => setNumDays(30)}>
        <View style={styles.dayStyle(range.numDays == 30)}>
          <Text
            variation={
              range.numDays == 30 ? 'Subtitle1Bold' : 'Subtitle1Regular'
            }
            color={range.numDays == 30 ? colors.accentStrong : colors.textMuted}>
            30 Days
          </Text>
        </View>
      </Pressable>
      <Pressable onPress={() => setNumDays(60)}>
        <View style={styles.dayStyle(range.numDays == 60)}>
          <Text
            variation={
              range.numDays == 60 ? 'Subtitle1Bold' : 'Subtitle1Regular'
            }
            color={range.numDays == 60 ? colors.accentStrong : colors.textMuted}>
            60 Days
          </Text>
        </View>
      </Pressable>
    </View>
  );
};

//UI to display individual notes
export const NotesSection = ({navigation, notes, saveNote}) => {
  const styles = useThemedStyles(makeStyles);
  //notes exist in readings, now we need to get those out as a list
  // DLog('NotesSection');
  let newNotes = [];
  notes.data.forEach(reading => {
    // DLog(reading);
    if (reading.heading) {
      //now we push the notes object with keys title, noteText, createAt
      newNotes.push({
        title: reading.heading,
        noteText: reading.patientNote,
        createAt: reading.measureAt,
      });
    }
  });

  return (
    <View style={styles.marginMinus8}>
      <Notes
        key="NotesSection"
        navigation={navigation}
        notes={newNotes}
        listView={true}
        showDateTime
        onChangeNotes={note => {
          saveNote(note);
        }}
      />
    </View>
  );
};

//(plain helper, not a component — the caller passes its themed styles)
const AllValuesSectionDayBlock = (navigation, day, notes, getData, styles) => {
  // DLog('AllValuesSectionDayBlock', day, notes);
  let newNotes = [];
  notes.forEach(reading => {
    // DLog(reading);
    if (reading.diabetesMeasurement?.bloodSugarCount) {
      //now we push the notes object with keys title, noteText, createAt
      newNotes.push({
        title: reading.diabetesMeasurement.bloodSugarCount + ' mg/dL',
        noteText: reading.patientNote,
        createAt: reading.measureAt,
        extraData: reading, //sending the reading as extradata. this key is only present in case of allValues=true
      });
    } else if (reading.heading) {
      //now we push the notes object with keys title, noteText, createAt
      newNotes.push({
        title: reading.heading,
        noteText: reading.patientNote,
        createAt: reading.measureAt,
        extraData: reading, //sending the reading as extradata. this key is only present in case of allValues=true
      });
    }
  });

  return (
    <View style={styles.marginMinus8} key={day}>
      <Notes
        key="AllValuesSection"
        navigation={navigation}
        notes={newNotes}
        allValues={true}
        title={day}
        getData={getData}
        showDateTime
        hideAddButton={true}
      />
    </View>
  );
};

//UI to display individual notes
export const AllValuesSection = ({navigation, notes, getData}) => {
  const styles = useThemedStyles(makeStyles);
  //notes exist in readings, now we need to get those out as a list
  // DLog('AllValuesSection');

  let notesSeparatedByDates = {};
  //separate the notes by date
  notes.data.forEach(reading => {
    const d = moment(new Date(reading.measureAt)).format('MMMM Do, YYYY');
    if (notesSeparatedByDates[d]) {
      notesSeparatedByDates[d].push(reading);
    } else {
      notesSeparatedByDates[d] = [reading];
    }
  });

  // DLog('notesSeparatedByDates', notesSeparatedByDates);
  let blocks = [];
  const today = moment(new Date()).format('MMMM Do, YYYY');
  //now iterate in the map
  for (let key in notesSeparatedByDates) {
    // DLog('key', key, key == today);
    // DLog('notesSeparatedByDates[key]', notesSeparatedByDates[key]);
    blocks.push(
      AllValuesSectionDayBlock(
        navigation,
        key == today ? 'Today' : key,
        notesSeparatedByDates[key],
        getData,
        styles,
      ),
    );
  }
  return blocks.reverse();
};
